import {
  slidesVisibleValuesDesktop,
  slidesVisibleValuesMobile,
} from "hec/components/ui/carousel.utils";

// Builder input fragments for the ImageTextCarouselGrid block. Kept in a plain
// (non-component) module so they can be imported without re-declaring, and so
// index.tsx stays a component-only module for react-refresh.
//
// `iconsHeaderTextInput` + `imageTextItemsInput` are the shared, dual-consumed
// fragments: both this standalone block AND the homepageCarouselGrid composite
// import them, so the item schema never drifts between the two (SFCC-11246 AC4).
// `slidesVisibleInput` / `slidePaddingInput` / `buttonNavigationColorInput` are
// standalone-only — the homepage composite keeps its own inline layout inputs
// (its `slidesVisible` carries an extra `onTablet` tier for the product panel).

export const iconsHeaderTextInput = {
  name: "iconsHeaderText",
  type: "string",
  defaultValue: "Popular Categories",
  helperText: "The header text for the icons grid.",
};

export const imageTextItemsInput = {
  name: "items",
  type: "list",
  subFields: [
    {
      name: "image",
      type: "file",
      helperText: "Image for the item",
    },
    {
      name: "isInternalLink",
      type: "boolean",
      defaultValue: true,
      helperText:
        'Select "true" if the link routes to a page in our storefront.',
    },
    {
      name: "pageReference",
      type: "reference",
      model: "page",
      showIf: `options.get('isInternalLink') === true`,
      helperText: "The internal page this item links to.",
    },
    {
      name: "externalLinkReference",
      type: "reference",
      model: "external-links",
      showIf: `options.get('isInternalLink') === false`,
      helperText: "The external link for this item.",
    },
    {
      name: "customUrl",
      friendlyName: "Custom URL",
      type: "text",
      required: false,
      helperText:
        "Optional. When filled, this URL is used instead of the page reference. " +
        '"Is internal link" (default ON) → in-app navigation; turn it OFF for ' +
        'external links (e.g. https://...); "Is SFRA" → full page load and ' +
        'overrides "Is internal link".',
    },
    {
      name: "customUrlIsSfra",
      friendlyName: "Is SFRA",
      type: "boolean",
      defaultValue: false,
      helperText:
        "Only applies when a Custom URL is set. Enable for SFRA-served pages " +
        "so the Custom URL performs a full page load instead of in-app " +
        'navigation. Takes precedence over "Is internal link".',
    },
    {
      name: "openInNewTab",
      type: "boolean",
      defaultValue: false,
      helperText: 'Select "true" to open the link in a new tab.',
    },
    {
      name: "text",
      type: "text",
      helperText: "The text for the icon item.",
    },
    {
      name: "badge",
      friendlyName: "Badge",
      type: "string",
      required: false,
      helperText:
        'Optional pill on the top-right corner of the item (e.g. "New!"). Leave empty for no badge.',
    },
    {
      name: "badgeBackgroundColor",
      friendlyName: "Badge background color",
      type: "color",
      required: false,
      showIf: `!!options.get('badge')`,
      helperText:
        "Optional. Background color of the badge pill. Leave empty to keep the site's default badge color.",
    },
  ],
};

export const slidesVisibleInput = {
  name: "slidesVisible",
  friendlyName: "Number of Slides Visible",
  type: "object",
  subFields: [
    {
      name: "onDesktop",
      type: "string",
      enum: slidesVisibleValuesDesktop,
      defaultValue: "3",
    },
    {
      name: "onMobile",
      type: "string",
      enum: slidesVisibleValuesMobile,
      defaultValue: "3",
    },
  ],
};

export const slidePaddingInput = {
  name: "slidePadding",
  friendlyName: "Padding Between Slides",
  type: "object",
  subFields: [
    {
      name: "onDesktop",
      type: "string",
      enum: Array.from({ length: 7 }, (_v, i) => i.toString()),
      defaultValue: "2",
      helperText: "Padding between slides",
    },
    {
      name: "onMobile",
      friendlyName: "Padding Between Slides Mobile",
      type: "string",
      enum: Array.from({ length: 7 }, (_v, i) => i.toString()),
      defaultValue: "2",
      helperText: "Padding between slides on mobile",
    },
  ],
};

export const buttonNavigationColorInput = {
  name: "ButtonNavigationColor",
  type: "color",
  friendlyName: "Button Navigation Color",
  helperText: "Color of the button navigation",
};
